import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineNotice } from "@/components/common/InlineNotice";
import { StatusChip } from "@/components/common/StatusChip";
import { BadgeList } from "@/components/common/Badges";
import { Check, CheckCircle2, Circle, Mail, Phone, ShieldCheck, CreditCard, Camera, Upload, UserCheck } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

function StepRow({ done, title, description, children, "data-testid": testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-start gap-3">
        {done ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] mt-0.5" /> : <Circle className="h-5 w-5 text-muted-foreground mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{title}</div>
          {description && <div className="text-xs text-muted-foreground">{description}</div>}
          {!done && <div className="mt-2">{children}</div>}
        </div>
      </div>
    </div>
  );
}

async function readFile(file) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
}

export default function AthleteVerifyPage() {
  const { user, setUser, refresh } = useAuth();
  const nav = useNavigate();
  const checklist = user?.trust_checklist || {};
  const trust = user?.trust_level || 0;

  // Phone
  const [phone, setPhone] = useState(user?.phone || "");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneSent, setPhoneSent] = useState(false);
  // Email
  const [emailSent, setEmailSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  // Photo
  const [photo, setPhoto] = useState(user?.photo || null);
  // Payment
  const [pmClientId, setPmClientId] = useState(null);
  // Level 2
  const [idPhoto, setIdPhoto] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [address, setAddress] = useState(user?.address || "");
  const [athleteVerif, setAthleteVerif] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/public/paypal-client-id`).then((r) => setPmClientId(r.data.client_id)).catch(() => {});
    api.get("/athlete/verify/status").then((r) => setAthleteVerif(r.data)).catch(() => {});
  }, []);

  async function startPhone() {
    try {
      const { data } = await api.post("/auth/phone/start", { phone });
      setPhoneSent(true);
      if (data.dev_code) toast.info(`Dev code: ${data.dev_code}`, { duration: 20000 });
      else toast.success("Code sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function verifyPhone() {
    try {
      const { data } = await api.post("/auth/phone/verify", { code: phoneCode });
      setUser(data.user); toast.success("Phone verified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Wrong code"); }
  }

  async function startEmail() {
    try { await api.post("/auth/email/start"); setEmailSent(true); toast.success("Verification code sent to your email"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function verifyEmail() {
    try { const { data } = await api.post("/auth/email/verify", { code: emailCode }); setUser(data.user); toast.success("Email verified"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Wrong code"); }
  }

  async function savePhoto() {
    try { const { data } = await api.patch("/profile", { photo }); setUser(data.user); toast.success("Photo saved"); }
    catch (e) { toast.error("Failed"); }
  }

  async function submitL2() {
    if (!idPhoto || !selfie) return toast.error("Upload both ID and selfie");
    try {
      const { data } = await api.post("/athlete/verify/upload", { id_photo: idPhoto, selfie, address });
      const finalStatus = data.status || data.verification_status;
      const sim = data.similarity;
      const idFlags = data?.id_check?.flags || [];
      if (finalStatus === "approved") {
        toast.success(`Verified! Face match ${sim ?? ""}%`);
      } else if (finalStatus === "rejected") {
        const reason = idFlags.includes("no_face_on_id")
          ? "We couldn't find a face on your ID — please upload a clearer photo of a government ID."
          : sim != null && sim < 90
            ? `Face match only ${sim}% — please retake your selfie.`
            : "Verification failed — please retake clearer photos.";
        toast.error("Verification failed", { description: reason });
      } else {
        toast.info("Submitted for admin review", { description: sim != null ? `Face match ${sim}%` : undefined });
      }
      const s = await api.get("/athlete/verify/status"); setAthleteVerif(s.data);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const allL1Done = useMemo(() => Object.values(checklist).every(Boolean), [checklist]);

  return (
    <div className="space-y-4 pb-8">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">Trust level</div>
              <div className="text-xl font-semibold bb-display">
                {trust === 2 ? "Verified Athlete" : trust === 1 ? "Basic (Level 1)" : "Unverified"}
              </div>
            </div>
            <BadgeList badges={trustBadges(trust, user)} max={4} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="bb-display">Level 1 · Basic signup</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <StepRow data-testid="trust-name-row" done={!!checklist.name} title="Full legal name" description={user?.name} />
          <StepRow data-testid="trust-tos-row" done={!!checklist.tos_accepted_at} title="Terms accepted" description="Required." />

          <StepRow data-testid="trust-email-row" done={!!checklist.email_verified} title="Email verification" description={user?.email}>
            {!emailSent ? (
              <Button size="sm" onClick={startEmail} data-testid="trust-email-send"><Mail className="h-4 w-4 mr-1" /> Send code</Button>
            ) : (
              <div className="flex gap-2">
                <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value)} placeholder="6-digit code" data-testid="trust-email-code" />
                <Button onClick={verifyEmail} data-testid="trust-email-verify">Verify</Button>
              </div>
            )}
          </StepRow>

          <StepRow data-testid="trust-phone-row" done={!!checklist.phone_verified} title="Mobile phone verification" description={user?.phone || "Add your phone"}>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" data-testid="trust-phone-input" />
                <Button onClick={startPhone} data-testid="trust-phone-send"><Phone className="h-4 w-4 mr-1" /> Send code</Button>
              </div>
              {phoneSent && (
                <div className="flex gap-2">
                  <Input value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} placeholder="6-digit code" data-testid="trust-phone-code" />
                  <Button onClick={verifyPhone} data-testid="trust-phone-verify">Verify</Button>
                </div>
              )}
            </div>
          </StepRow>

          <StepRow data-testid="trust-photo-row" done={!!checklist.photo} title="Profile photo" description="So ball boys can recognize you.">
            <div className="flex items-center gap-3">
              {photo && <img src={photo} alt="preview" className="h-14 w-14 rounded-full object-cover border border-border" />}
              <label className="inline-flex items-center gap-2 text-xs text-primary underline cursor-pointer">
                <Camera className="h-4 w-4" /> Choose photo
                <input hidden type="file" accept="image/*" data-testid="trust-photo-input" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return; setPhoto(await readFile(f));
                }} />
              </label>
              <Button size="sm" onClick={savePhoto} disabled={!photo} data-testid="trust-photo-save">Save</Button>
            </div>
          </StepRow>

          <StepRow data-testid="trust-payment-row" done={!!checklist.payment_method_verified} title="Payment method on file" description="We charge $0.01 (sandbox) to confirm your PayPal account.">
            {pmClientId ? (
              <PayPalScriptProvider options={{ "client-id": pmClientId, currency: "USD", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay" }}
                  createOrder={async () => {
                    const { data } = await api.post("/payment-method/order");
                    return data.order_id;
                  }}
                  onApprove={async (data) => {
                    await api.post("/payment-method/capture", { order_id: data.orderID });
                    toast.success("Payment method confirmed");
                    await refresh();
                  }}
                  onError={(err) => toast.error("PayPal error: " + err)}
                />
              </PayPalScriptProvider>
            ) : (
              <div className="text-xs text-muted-foreground">Loading checkout…</div>
            )}
          </StepRow>
        </CardContent>
      </Card>

      {allL1Done && (
        <Card>
          <CardHeader>
            <CardTitle className="bb-display flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" /> Level 2 · Verified Athlete (optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {athleteVerif?.status === "approved" ? (
              <InlineNotice tone="success" title={`Verified Athlete approved${athleteVerif?.match?.similarity != null ? ` · ${athleteVerif.match.similarity}% face match` : ""}`}>You'll now unlock late-night / private-location / high-value bookings.</InlineNotice>
            ) : athleteVerif?.status === "pending" ? (
              <InlineNotice tone="info" title={`Under review${athleteVerif?.match?.similarity != null ? ` · ${athleteVerif.match.similarity}% face match` : ""}`}>Our team is reviewing your ID and selfie.</InlineNotice>
            ) : athleteVerif?.status === "rejected" ? (
              <InlineNotice tone="destructive" title="Rejected">{athleteVerif.reason || athleteVerif?.match?.reason || "Please re-upload clearer photos."}</InlineNotice>
            ) : (
              <p className="text-sm text-muted-foreground">Upload a government ID plus a selfie. Our AI compares faces instantly; most athletes are approved in seconds.</p>
            )}

            {athleteVerif?.status !== "approved" && (
              <>
                <div>
                  <Label className="mb-1 block">Government ID photo</Label>
                  <input type="file" accept="image/*" onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return; setIdPhoto(await readFile(f));
                  }} data-testid="athlete-id-photo-input" />
                  {idPhoto && <img alt="id" src={idPhoto} className="mt-2 max-h-40 rounded-lg border border-border" />}
                </div>
                <div>
                  <Label className="mb-1 block">Selfie</Label>
                  <input type="file" accept="image/*" capture="user" onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return; setSelfie(await readFile(f));
                  }} data-testid="athlete-selfie-input" />
                  {selfie && <img alt="selfie" src={selfie} className="mt-2 max-h-40 rounded-lg border border-border" />}
                </div>
                <div>
                  <Label className="mb-1 block">Address (optional)</Label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="athlete-address-input" />
                </div>
                <Button onClick={submitL2} disabled={!idPhoto || !selfie} className="w-full" data-testid="athlete-verify-submit">
                  <Upload className="h-4 w-4 mr-1" /> Submit for verification
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Button variant="secondary" className="w-full" onClick={() => nav("/app")} data-testid="trust-done">Back to home</Button>
    </div>
  );
}

function trustBadges(trust, user) {
  const out = [];
  if (trust >= 1) out.push({ id: "basic_verified", label: "Basic Verified", icon: "shield-check", tone: "info" });
  if (trust >= 2) out.push({ id: "verified_athlete", label: "Verified Athlete", icon: "shield-check", tone: "success" });
  if (user?.athlete_verification_status === "pending") out.push({ id: "pending_v", label: "Verification pending", icon: "shield", tone: "warning" });
  return out;
}
